import type {
  CareStateInternalNoteT,
  CareStateInternalNoteTLink,
  CareStateProblemT,
  CareStateRequestT,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import { useCallback, useMemo } from 'react';

import { useMyIdentificationQuery } from '@/entities/identification';

import type { CareStateUpdate } from '@/entities/care-state-engine';
import { analytics, decodeLotusUuidTimestampMs, generateClientId } from '@/shared/lib';

import { useCareStateDomain } from '../ui/CareStateProvider';
import type { CareStateDomainDb } from '../model/types';
import type { CareStateCommand, CareStateCommandExecutor, CareStateCommandMeta } from './types';

function normalizeTitle(raw: string): string {
  return raw.trim();
}

type TableName = 'patients' | 'problems' | 'requests';
type AnalyticsTrackableRequestStatus = Extract<
  CareStateRequestT['status'],
  'Open' | 'Resolved' | 'Cancelled'
>;

type PatientRow = CareStateDomainDb['patients'][number];
type ProblemRow = CareStateDomainDb['problems'][number];
type RequestRow = CareStateDomainDb['requests'][number];
type UpdatableRow = PatientRow | ProblemRow | RequestRow;

function requestAgeMsFromId(requestId: string): number | undefined {
  const createdAtMs = decodeLotusUuidTimestampMs(requestId);
  if (createdAtMs === null) return undefined;
  return Math.max(0, Date.now() - createdAtMs);
}

function updateField<T extends UpdatableRow>(
  dispatch: (u: CareStateUpdate<CareStateDomainDb>) => void,
  table: TableName,
  existing: T | null,
  updater: (existing: T) => T
): void {
  if (existing === null) return;
  const next = updater(existing);
  dispatch({
    type: 'update',
    table,
    row: next,
    conditionalOldValue: existing,
  });
}

function deleteEntity(
  dispatch: (u: CareStateUpdate<CareStateDomainDb>) => void,
  table: TableName,
  existing: UpdatableRow | null,
  id: string
): void {
  if (existing === null) return;
  dispatch({
    type: 'delete',
    table,
    id,
    conditionalOldValue: existing,
  });
}

function toAnalyticsTrackableRequestStatus(
  status: CareStateRequestT['status']
): AnalyticsTrackableRequestStatus | null {
  switch (status) {
    case 'Open':
    case 'Resolved':
    case 'Cancelled':
      return status;
    case 'Pending Follow Up':
    default:
      return null;
  }
}

export function useCareStateCommands(): CareStateCommandExecutor {
  const { careState, patientUserId, scope, update } = useCareStateDomain();
  const identificationQuery = useMyIdentificationQuery();

  const dispatch = useCallback(
    (u: CareStateUpdate<CareStateDomainDb>) => {
      update(u);
    },
    [update]
  );

  // ZEKKA NOTE: I've introduced expiry to the setNote handlers, but to preserve the old behavior, I've set expiry to `null`
  // If you actually want expiry behavior, go ahead and change this!
  const execute = useCallback(
    (cmd: CareStateCommand, meta?: CareStateCommandMeta) => {
      if (careState === null) return;
      const actor = meta?.actor ?? 'clinician';

      switch (cmd.type) {
        case 'patient.setNote': {
          const existing = careState.patients.find((p) => p.id === 'root') ?? null;
          const previousLength = existing?.note?.value.length ?? 0;
          const nextLength = cmd.note.length;
          const next: CareStateDomainDb['patients'][number] = {
            id: 'root',
            note: {
              value: cmd.note,
              expiry: {
                basis: null,
                time: null,
              },
            },
          };

          if (existing === null) {
            dispatch({ type: 'create', table: 'patients', row: next });
            void analytics.track({
              name: 'carestate.patient.note_saved',
              context: { actor, patientUserId, scope },
              properties: {
                noteLength: nextLength,
                noteDeltaLength: nextLength,
              },
            });
            return;
          }

          updateField(dispatch, 'patients', existing, () => next);
          void analytics.track({
            name: 'carestate.patient.note_saved',
            context: { actor, patientUserId, scope },
            properties: {
              noteLength: nextLength,
              noteDeltaLength: nextLength - previousLength,
            },
          });
          return;
        }

        case 'problem.create': {
          const title = normalizeTitle(cmd.title);
          if (title.length === 0) return;

          const row: CareStateProblemT = {
            id: generateClientId(),
            title,
            note: {
              value: '',
              expiry: {
                basis: null,
                time: null,
              },
            },
          };
          dispatch({ type: 'create', table: 'problems', row });
          return;
        }

        case 'problem.rename': {
          const title = normalizeTitle(cmd.title);
          if (title.length === 0) return;

          const existing = careState.problems.find((p) => p.id === cmd.problemId) ?? null;
          updateField(dispatch, 'problems', existing, (p) => ({ ...p, title }));
          return;
        }

        case 'problem.delete': {
          const existing = careState.problems.find((p) => p.id === cmd.problemId) ?? null;
          deleteEntity(dispatch, 'problems', existing, cmd.problemId);
          return;
        }

        case 'problem.setNote': {
          const existing = careState.problems.find((p) => p.id === cmd.problemId) ?? null;
          const previousLength = existing?.note?.value.length ?? 0;
          const nextLength = cmd.note.length;
          updateField(dispatch, 'problems', existing, (p) => ({
            ...p,
            note: {
              value: cmd.note,
              expiry: {
                basis: null,
                time: null,
              },
            },
          }));
          if (existing !== null) {
            void analytics.track({
              name: 'carestate.problem.note_saved',
              context: { actor, patientUserId, scope },
              properties: {
                problemId: cmd.problemId,
                noteLength: nextLength,
                noteDeltaLength: nextLength - previousLength,
              },
            });
          }
          return;
        }

        case 'internalNote.create': {
          const note = cmd.note.trim();
          if (note.length === 0) return;

          const clinicianId = identificationQuery.data?.userId;
          if (clinicianId === undefined) return;

          const link: CareStateInternalNoteTLink =
            scope.type === 'patient'
              ? { type: 'patient' }
              : scope.type === 'problem'
                ? { type: 'problem', id: scope.id }
                : { type: 'request', id: scope.id };

          const row: CareStateInternalNoteT = {
            id: generateClientId(),
            note: {
              value: note,
              expiry: {
                basis: null,
                time: null,
              },
            },
            created_by: clinicianId,
            link,
          };

          dispatch({ type: 'create', table: 'internal_notes', row });
          void analytics.track({
            name: 'internal_note.created',
            context: { actor, patientUserId, scope },
            properties: {
              linkType: scope.type,
              linkId: scope.type === 'patient' ? undefined : scope.id,
              noteLength: note.length,
            },
          });
          return;
        }

        case 'request.create': {
          const title = normalizeTitle(cmd.title);
          if (title.length === 0) return;

          const row: CareStateRequestT = {
            id: generateClientId(),
            title,
            note: {
              value: '',
              expiry: {
                basis: null,
                time: null,
              },
            },
            status: 'Open',
            urgency: 'Routine',
            assignee: 'Clinician',
            link: { type: 'problem', id: cmd.problemId },
          };
          dispatch({ type: 'create', table: 'requests', row });
          return;
        }

        case 'request.rename': {
          const title = normalizeTitle(cmd.title);
          if (title.length === 0) return;

          const existing = careState.requests.find((r) => r.id === cmd.requestId) ?? null;
          updateField(dispatch, 'requests', existing, (r) => ({ ...r, title }));
          return;
        }

        case 'request.delete': {
          const existing = careState.requests.find((r) => r.id === cmd.requestId) ?? null;
          deleteEntity(dispatch, 'requests', existing, cmd.requestId);
          return;
        }

        case 'request.setNote': {
          const existing = careState.requests.find((r) => r.id === cmd.requestId) ?? null;
          const previousLength = existing?.note?.value.length ?? 0;
          const nextLength = cmd.note.length;
          updateField(dispatch, 'requests', existing, (r) => ({
            ...r,
            note: {
              value: cmd.note,
              expiry: {
                basis: null,
                time: null,
              },
            },
          }));
          if (existing !== null) {
            void analytics.track({
              name: 'carestate.request.note_saved',
              context: { actor, patientUserId, scope },
              properties: {
                requestId: cmd.requestId,
                noteLength: nextLength,
                noteDeltaLength: nextLength - previousLength,
              },
            });
          }
          return;
        }

        case 'request.setStatus': {
          const existing = careState.requests.find((r) => r.id === cmd.requestId) ?? null;
          updateField(dispatch, 'requests', existing, (r) => ({ ...r, status: cmd.status }));
          if (existing !== null && existing.status !== cmd.status) {
            const oldStatus = toAnalyticsTrackableRequestStatus(existing.status);
            const newStatus = toAnalyticsTrackableRequestStatus(cmd.status);
            if (oldStatus === null || newStatus === null) {
              return;
            }
            void analytics.track({
              name: 'carestate.request.status_changed',
              context: { actor, patientUserId, scope },
              properties: {
                requestId: cmd.requestId,
                requestAgeMs: requestAgeMsFromId(cmd.requestId),
                oldStatus,
                newStatus,
              },
            });
          }
          return;
        }

        case 'request.setUrgency': {
          const existing = careState.requests.find((r) => r.id === cmd.requestId) ?? null;
          updateField(dispatch, 'requests', existing, (r) => ({ ...r, urgency: cmd.urgency }));
          if (existing !== null && existing.urgency !== cmd.urgency) {
            void analytics.track({
              name: 'carestate.request.urgency_changed',
              context: { actor, patientUserId, scope },
              properties: {
                requestId: cmd.requestId,
                oldUrgency: existing.urgency,
                newUrgency: cmd.urgency,
              },
            });
          }
          return;
        }

        default: {
          const _exhaustive: never = cmd;
          return _exhaustive;
        }
      }
    },
    [careState, dispatch, identificationQuery.data, patientUserId, scope]
  );

  return useMemo(() => ({ execute }), [execute]);
}
